// `configureDictationBubblePorts` — the app capabilities the web dictation
// bubble, its zustand store, anonymous access and the re-engagement hint read
// at call or render time. Same shape as `dictation-bridge-ports.ts`: a
// fail-loud getter over a module-held reference, and a test reset.
//
// Every member is read inside a function body, never at module scope: the
// store module evaluates in any graph that imports `.`, so a module-scope read
// would throw before `src/composition/hosts/dictation/dictation-data-wiring.ts` has run.
import type { Editor } from '@tiptap/react';

import type { DictationContextResponse } from '../../contracts/dictation-context.types';
import type { T11nHttpClient } from '../../data/config';

export interface DictationBubblePorts {
  /** The user's dictation hotkey chord, outside React. */
  getDictationHotkey: () => string[];
  /** The user's dictation hotkey chord, as a hook. */
  useDictationHotkey: () => { hotkey: string[] };
  /** Whether dictation is enabled for this user and platform, outside React. */
  isDictationEnabled: () => boolean;
  /** Whether dictation is enabled for this user and platform, as a hook. */
  useIsDictationEnabled: () => boolean;
  /** Arms the app's audio-level monitoring for the bars. */
  setReadyToRequestMicPermissions: () => void;
  /** Whether this build profile ships product feedback (off on-prem). */
  isProductFeedbackAvailable: () => boolean;
  /** Whether the app-level increased-cadence and platform gates are enabled. */
  isIncreasedFeedbackCadenceEnabled: () => boolean;
  /** Fetches current billing and resolves free, non-managed eligibility. */
  resolveIsFreeNonManagedUser: () => Promise<boolean>;
  /** Whether dictation chunks go to the logs backend. */
  isDictationLogsBackendEnabled: () => boolean;
  /** The dictation-context flag (fetch-time read). */
  isDictationContextEnabled: () => boolean;
  /** The dictation log opt-out flag (fetch-time read). */
  isDictationLogOptOutEnabled: () => boolean;
  /** `GET /dictation-post-processing/context`. */
  fetchDictationContext: (
    signal?: AbortSignal
  ) => Promise<{ data: DictationContextResponse }>;
  /** Loads the logs-backend (v2) transcription client on first use. */
  loadT11nHttpV2: () => Promise<T11nHttpClient>;
  /** The transcription base URL for anonymous access, or `null` when unset. */
  resolveAnonymousTranscriptionBaseUrl: () => Promise<string | null>;
  /** Whether a Chronicle recording is in flight. */
  isChronicleRecording: () => boolean;
  /** Whether a mutation key belongs to one of the session-create mutations. */
  isSessionCreateMutationKey: (key: unknown) => boolean;
  /** Whether any dictation surface is active. */
  useIsAnyDictationActive: () => boolean;
  /** Whether a clinical recording is in flight. */
  useIsClinicalRecordingActive: (isTimerRunning: boolean) => boolean;
  /** Whether the editor is still mounted, reporting post-destroy access. */
  isEditorLive: (
    editor: Editor | null | undefined,
    callSite?: string
  ) => editor is Editor;
  /** The PostHog-resolved re-engagement flag. */
  useIsDictateReengagementEnabled: () => boolean;
}

let ports: DictationBubblePorts | null = null;

export const configureDictationBubblePorts = (next: DictationBubblePorts): void => {
  ports = next;
};

/** Fail loudly on unconfigured use — never silently degrade. */
export const getDictationBubblePorts = (): DictationBubblePorts => {
  if (!ports) {
    throw new Error(
      '[dictate/ui/bubble] ports not configured — the app must call ' +
        'configureDictationBubblePorts() at bootstrap ' +
        "(see '@/composition/hosts/dictation/dictation-data-wiring'); tests must configure a double first."
    );
  }
  return ports;
};

export const resetDictationBubblePortsForTests = (): void => {
  ports = null;
};
